import { USER_ROLES } from "commonComponents/LeftMenu/menuItemsData";
import { MIN_NW_APP_VERSION_SUPPORTED, SOFT_LOCK_ACTIVE } from "data/constants";
import { isItemAvailableInSchedule } from "helper/catalogueHelpers";
import { getVersionNumber } from "helper/versionHelper";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useState, useEffect } from 'react';

// Moved to @rista/core/catalog — re-exported so this file's importers are unchanged.
// It is shared now because MOBILE runs the same catalogue projection for the Suggestive Selling
// flow; the core version also memoises on the filter FLAGS rather than the config object's
// identity, so an inline `{...}` literal no longer defeats the memo.
export { useCatalogItemsFromIds } from "@rista/core/catalog/hooks/usePosCatalogItems";
export type { CatalogItemsFilterConfig } from "@rista/core/catalog/posCard";

/**
 * 
 * @param itemId 
 * @returns true if this item is available in current schedule
 */
export const useItemAvailableInSchedule = (itemId: string) => {
  const itemsData = useSelector((state: any) => state.itemsStore.itemsData);
  const itemSchedules = useSelector((state: any) => state.catalogStore?.scheduleData);
  const branchTimeZoneId = useSelector(
    (state: any) => state.businessStore?.businessData.timeZone.zoneId,
  );
  const selectedChannelDefId = useSelector(
    (state: any) => state.saleDataStore?.selectedChannelData?.channelDefId,
  );
  const isAvailableInSchedule = useMemo(() => {
    return isItemAvailableInSchedule(itemId, {
      itemsData,
      branchTimeZoneId,
      channelDefId: selectedChannelDefId,
      itemSchedules,
    });
  }, [itemsData, branchTimeZoneId, selectedChannelDefId, itemSchedules]);
  return isAvailableInSchedule;
};

export const useCheckForChatSupport = () => {
  const chatSupportAccess = useSelector((state : any) => state.myOrderStore.chatSupportAccess);
  const hasChatSupport = chatSupportAccess?.some((data : Record<string, boolean>) => data?.isChatEnable) ?? false;
  return hasChatSupport;
}

export const useEnableNotificationPolling = () => {
  const enableNotificationPolling = useMemo(() => {
    const versionNumber = getVersionNumber();
    return !isNaN(versionNumber) && versionNumber < MIN_NW_APP_VERSION_SUPPORTED.NW_WEBSOCKET_IMPLEMENTATION_VERSION;
  }, []);
  return enableNotificationPolling
}

export const useNotAdmin = () => {
    const userData = useSelector((state: any) => state.businessStore?.userData);
    const isAdmin = useMemo(() => userData?.role !== USER_ROLES.ADMIN, [userData])
    return isAdmin
}

export const useOfflineState = () => {
  const isOffline = useSelector((state: any) => state.businessStore.isOffline);
  const itemsData = useSelector((state: any) => state.itemsStore?.itemsData || {});
  const channelDef = useSelector((state: any) => state.catalogStore?.channelDef || []);
  const [fetchDataFromDb, setFetchDataFromDb] = useState(false);

  return { isOffline, itemsData, channelDef, fetchDataFromDb, setFetchDataFromDb };
}

export const useLoginTimer = (initialSeconds: number = 30) => {
  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const [isTimerOn, setIsTimerOn] = useState(false);
  const [leftAttemptCount, setLeftAttemptCount] = useState<number>(-1);

  const triggerSoftLock = useCallback(() => {
    setTimeLeft(initialSeconds);
    setIsTimerOn(true);
  }, [initialSeconds]);

  useEffect(() => {
    if (leftAttemptCount === SOFT_LOCK_ACTIVE) {
      triggerSoftLock();
    }
  }, [leftAttemptCount, triggerSoftLock]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;

    if (isTimerOn && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    } else if (!timeLeft) {
      setIsTimerOn(false);
    }

    return () => clearInterval(interval);
  }, [isTimerOn, timeLeft]);

  return {
    timeLeft,
    isTimerOn,
    leftAttemptCount,
    setLeftAttemptCount,
    triggerSoftLock 
  };
};
/**
 * Whether loyalty enrolment consent must be collected for the current merchant, provider and
 * customer.
 *
 * ⚠️ **Moved to `@rista/core/loyalty/hooks` (`useIsConsentMandatory`)** on 2026-09-04 and
 * re-exported here so this path's one importer is unchanged. The rule itself is
 * `isConsentMandatory` in `@rista/core/loyalty/consent`, unit-tested there. New code should
 * import from `@rista/core/loyalty/hooks`.
 */
export { useIsConsentMandatory as useIsLoyaltyCustomerConsentRequired } from "@rista/core/loyalty/hooks";

export const useIsPostSaleCommunicationEnabled = () => {
  const postSaleCommunication = useSelector(
    (state: any) => state.businessStore?.businessData?.postSaleCommunication
  );
  return {
    isEnabled: !!postSaleCommunication?.enabled,
    mediums: postSaleCommunication?.mediums || []
  };
};
